import Pusher from 'pusher-js'
import constants from "@/constants";
import HeartbeatService from "@/services/heartbeat";
import { isRuHostname, ruHostname } from "@/services/region";

const mixin = {
  data () {
    return {
      waitForLaunchInterval: null,
      // Any host in the RU zone, not the single name `layer.apiqlive.ru` this
      // used to compare against. That equality meant a second RU entry point -
      // another subdomain, a staging name, the host reached with a trailing dot
      // - served global URLs with nothing reporting it, which is the exact
      // failure the mirror exists to prevent.
      isRuVersion: isRuHostname(window.location.hostname),
      metaChannelPrefix: '',
      channels: {},
      _localHandlers: new Map(),
      _isInit: false,
      heartbeat: null,
    }
  },
  computed: {
    userLink () {
      return this.$route.params.userlink
    },
    metaChannelName () {
      return `${this.metaChannelPrefix}.${this.userLink}`
    },
    // Both of these are bare hostnames rather than URLs, so they map by host
    // rather than through the URL parser the media path uses.
    pusherHost () {
      return this.isRuVersion ? ruHostname(constants.PUSHER.HOST) : constants.PUSHER.HOST
    },
    backendHost () {
      return this.isRuVersion ? ruHostname(constants.BACKEND_HOST) : constants.BACKEND_HOST
    }
  },
  methods: {
    initPusher (metaChannelPrefix = 'presence-layer-meta') {
      if (this._isInit && this.pusher) return this.pusher;
      this._isInit = true;

      // pusher-js 7.x remembers even refused HTTP handshakes for 30 minutes.
      // Discard those entries before the constructor starts connecting.
      for (const key of ['pusherTransportTLS', 'pusherTransportNonTLS']) {
        try {
          const cached = JSON.parse(localStorage.getItem(key))
          if (cached && !['ws', 'wss'].includes(cached.transport)) localStorage.removeItem(key)
        } catch (e) { /* Storage may be unavailable in OBS. */ }
      }

      this.pusher = new Pusher(constants.PUSHER.APP_KEY, {
        wsHost: this.pusherHost,
        wsPath: constants.PUSHER.PATH,
        wssPort: constants.PUSHER.PORT,
        wsPort: constants.PUSHER.PORT,
        forceTLS: true,
        disableStats: true,
        // Self-hosted WebSocket endpoint; HTTP/SockJS support is not configured.
        // XHR fallbacks otherwise use the library's sockjs.pusher.com default.
        enabledTransports: ['ws', 'wss'],
        // A socket can go silently dead without a close frame (some RU ISPs
        // drop long-lived TLS mid-stream) while soketi still shows it
        // occupied. Default timeouts left this undetected for ~58s, losing
        // any launch dispatched in that window; tightened so the client
        // notices and reconnects within ~20-25s instead.
        activityTimeout: 15000,
        pongTimeout: 8000,
        channelAuthorization: {
          endpoint: `${this.backendHost}/pusher-presence-auth`
        }
      })

      this.metaChannelPrefix = metaChannelPrefix

      const markReload = (reason) => {
        sessionStorage.setItem('needs_reload', '1')
        localStorage.setItem('ws_last_reload_reason', `${reason} @ ${new Date().toISOString()}`)
      }

      const conn = this.pusher.connection
      // 'unavailable' has automatic retries; 'disconnected' is terminal in
      // pusher-js 7.x (including a refused handshake). Reuse this client so
      // page-level launch handlers survive and channels are re-subscribed.
      this._pusherRetryDelay = 1000
      conn.bind('disconnected', () => {
        if (!this._isInit || this._pusherRetryTimer != null) return
        this._pusherRetryTimer = setTimeout(() => {
          this._pusherRetryTimer = null
          if (this._isInit && conn.state === 'disconnected') this.pusher.connect()
        }, this._pusherRetryDelay)
        this._pusherRetryDelay = Math.min(this._pusherRetryDelay * 2, 30000)
      })
      conn.bind('connected', () => {
        clearTimeout(this._pusherRetryTimer)
        this._pusherRetryTimer = null
        this._pusherRetryDelay = 1000
      })
      conn.bind('failed', () => markReload('failed'))

      this.addEventsLogging()

      const requestSafeReload = () => sessionStorage.setItem('needs_reload','1')

      // Public/common channel (subscribe once)
      const common = this.subscribeChannel('common-events')
      common.unbind(constants.EVENTS.ALL_LAYERS_REFRESHED)
      common.bind(constants.EVENTS.ALL_LAYERS_REFRESHED, requestSafeReload)

      // Presence meta channel (subscribe once)
      const meta = this.subscribeChannel(this.metaChannelName)
      meta.unbind('client-ping-is-alive')
      meta.bind('client-ping-is-alive', this._boundPing ??= () => {
        this.channels[this.metaChannelName]?.trigger('client-pong-is-alive', {
          userAgent: window.navigator.userAgent
          // ip ?
        })
      })
      meta.unbind(constants.EVENTS.LAYER_REFRESHED);
      meta.bind(constants.EVENTS.LAYER_REFRESHED, requestSafeReload)

      // Clean up on tab close (stable ref so disconnect() can remove it)
      this._onBeforeUnload = () => this.disconnect()
      window.addEventListener('beforeunload', this._onBeforeUnload)

      // Note: We no longer tear down Pusher on 'offline'. Pusher's client has
      // built-in reconnection and will automatically re-subscribe to channels
      // when the network returns. Tearing down was causing the page-level
      // task-launch channel subscriptions to be lost permanently, since those
      // are only bound once in mounted().
      this._offNet = () => {}

      return this.pusher
    },

    enableWindowSizeTracking (layerName) {
      const reqEvt = `client-track-size-req-${layerName}`
      const respEvt = `client-track-size-resp-${layerName}`

      // bind once and return unbinder
      return this.on(this.metaChannelName, reqEvt, () => {
        this.channels[this.metaChannelName]?.trigger(respEvt, {
          userAgent: navigator.userAgent,
          width: window.innerWidth,
          height: window.innerHeight
        })
      })
    },

    addEventsLogging () {
      const { connection } = this.pusher

      // Socket history since this page loaded. The heartbeat reports a moment;
      // this is what makes a moment mean something - a connection that is up now
      // but has fallen nine times is not the one that never fell.
      //
      // Instance fields rather than data(), and deliberately: Vue 2 does not
      // proxy keys beginning with an underscore onto the instance, so a counter
      // declared there reads back undefined and increments to NaN. That is why
      // _localHandlers is re-created defensively everywhere it is touched.
      this._wsDrops = 0
      this._wsConnectedAt = null

      // Counted here rather than in a second bind, so there is one place that
      // knows what a state change means.
      connection.bind('state_change', (s) => {
        if (s.current === 'connected') this._wsConnectedAt = Date.now()
        if (s.previous === 'connected' && s.current !== 'connected') this._wsDrops = (this._wsDrops || 0) + 1
      })

      const loggers = {
        error: (error) => console.error('[pusher] connection error', error),
        connected: () => console.warn('[pusher] connected event'),
        disconnected: () => console.warn('[pusher] disconnected event'),
        state_change: (s) => console.warn('[pusher] channels state', s.previous, '→', s.current)
      }

      Object.entries(loggers).forEach(([event, logger]) => {
        connection.bind(event, logger)
      })
    },

    subscribeChannel (name) {
      if (!this.channels) this.channels = {}
      if (!this._localHandlers) this._localHandlers = new Map()
      if (!this.pusher) this.initPusher()
      if (this.channels[name]) return this.channels[name]

      const ch = this.pusher.subscribe(name)
      this.channels[name] = ch
      if (!this._localHandlers.has(name)) this._localHandlers.set(name, new Map())
      return ch
    },

    // Safer bind with off() function
    on (channelName, eventName, handler) {
      if (!this._localHandlers) this._localHandlers = new Map()
      const ch = this.subscribeChannel(channelName)
      const key = `${eventName}::${handler.name || Math.random()}`
      const bound = (payload) => handler(payload)
      ch.bind(eventName, bound)
      this._localHandlers.get(channelName).set(key, { eventName, bound })
      return () => this.off(channelName, key)
    },

    off (channelName, key) {
      if (!this._localHandlers) this._localHandlers = new Map()
      const ch = this.channels[channelName]
      if (!ch) return
      const map = this._localHandlers.get(channelName)
      const item = map?.get(key)
      if (item) {
        ch.unbind(item.eventName, item.bound)
        map.delete(key)
      }
      // if that channel has no more handlers, leave it
      if (map && map.size === 0) {
        this.pusher.unsubscribe(channelName)
        this._localHandlers.delete(channelName)
        delete this.channels[channelName]
      }
    },

    /**
     * What the socket is doing, as of this beat.
     *
     * The heartbeat is an HTTP POST and has always said only that the machine is
     * alive and has network. Measured 06.09.2026: of 52 launches sent to layers
     * that acknowledge them, 6 produced no receipt at all, and every one of those
     * streamers was beating normally throughout - twitch user 9090 acknowledged
     * launches at 07:03 and 07:23 UTC, then nothing at 07:38, 08:01 and 08:20,
     * with the beat unbroken every three minutes across all of it. Nothing in the
     * layer dropped those events: receipts and mounts match exactly, so they never
     * arrived. The socket is the only unwatched segment of that path, and it is
     * unwatched everywhere - soketi writes no log at all, the wss host ships
     * nothing to Graylog, and the occupancy webhooks are not recorded either.
     *
     * `subscribed` is the field that separates the two failures worth telling
     * apart: a connection that is down, and a connection that is up while the
     * launch channel is not on it - which is what a re-subscribe that never
     * completed looks like, and it is invisible from the connection state alone.
     */
    socketState () {
      const conn = this.pusher && this.pusher.connection
      const launch = typeof this.getLaunchChannel === 'function'
        ? this.channels[this.getLaunchChannel()]
        : null

      return {
        socket_state: conn ? conn.state : 'absent',
        // Report the selected transport independently of the connection state.
        socket_transport: conn && conn.connection && conn.connection.transport
          ? conn.connection.transport.name || null
          : null,
        socket_id: conn && conn.socket_id ? String(conn.socket_id) : null,
        // The first beat is sent from mounted() before initPusher runs, so these
        // have to read as zero rather than as nothing.
        socket_drops: this._wsDrops || 0,
        // How long the connection has been up, and only while it is: measured
        // from the last time it reached 'connected', this keeps growing after a
        // drop and would read as uptime on a socket that is down.
        socket_up_ms: (conn && conn.state === 'connected' && this._wsConnectedAt)
          ? Date.now() - this._wsConnectedAt
          : null,
        launch_subscribed: launch ? !!launch.subscribed : false,
        launch_events_seen: this._launchEventsSeen || 0
      }
    },

    startHeartbeat (opts = {}) {
      if (!this.heartbeat) {
        this.heartbeat = new HeartbeatService()
      }

      this.heartbeat.start({
        endpoint: `${this.backendHost}/pusher-beat`,
        screenId: opts.screenId || this.userLink,
        userId: opts.userId || null,
        layer: opts.layer || 'overlay',
        intervalMs: opts.intervalMs || 60000,
        route: this.$route ? (this.$route.fullPath || this.$route.path) : null,
        state: () => this.socketState()
      })
    },

    stopHeartbeat () {
      if (this.heartbeat) {
        this.heartbeat.stop()
      }
    },

    disconnect () {
      // Suppress recovery before our own disconnect emits its terminal event.
      this._isInit = false
      clearTimeout(this._pusherRetryTimer)
      this._pusherRetryTimer = null
      if (typeof this.clearLayerTimers === 'function') {
        try { this.clearLayerTimers() } catch (e) { /* noop */ }
      }
      if (this._onBeforeUnload) {
        window.removeEventListener('beforeunload', this._onBeforeUnload)
        this._onBeforeUnload = null
      }
      this.stopHeartbeat()
      // Unbind all events and leave channels
      for (const [name, map] of (this._localHandlers || new Map()).entries()) {
        const ch = this.channels[name]
        for (const {eventName, bound} of map.values()) ch?.unbind(eventName, bound)
        this.pusher?.unsubscribe(name)
      }
      this._localHandlers?.clear()
      this.channels = {}
      this.pusher?.disconnect()
      try { this._offNet && this._offNet() } catch (e) { void e }
      this._offNet = null
      this._isInit = false
      this.pusher = null
    }
  }
}

mixin.beforeDestroy = function () {
  this.stopHeartbeat()
  this.disconnect && this.disconnect()
}

export default mixin
